import { css, cx } from "@emotion/css"
import { Loading } from "components/Loading"
import { GenericTableRow } from "../../types"
import { ArrowDropDown } from "@material-ui/icons"
import { ReactNode, useMemo } from "react"
import { standardScrollBar } from "../../styles"
import { EmptyResponse } from "./EmptyResponse"
import { Divider } from "@material-ui/core"

const Layout = ({
  isSortableTable,
  isContentReady,
  children,
}: {
  isSortableTable: boolean
  isContentReady: boolean
  children: ReactNode
}) => {
  return (
    <div className={isSortableTable ? styles.tableMapView : styles.table}>
      {isContentReady ? children : <Loading />}
    </div>
  )
}

export type RowDefinitionType = {
  name: string
  header: string
  render: (row: any, rowId: number) => string | JSX.Element
  customWidth?: number
  isColored?: boolean
  align?: string
  sortBy?: string
}

type TableProps = {
  rows: RowDefinitionType[]
  data?: any
  isLoading?: boolean
  hasError?: boolean
  columnWidth: number
  gap: number
  cellsPerColumn: number
  onSort?: (path: string) => void
  selectedSortField?: string
  selectedSortOrder?: string
  total?: any | null
}

export function BaseTable(props: TableProps) {
  const {
    rows,
    data,
    isLoading,
    hasError,
    columnWidth,
    gap,
    cellsPerColumn,
    onSort,
    selectedSortField,
    selectedSortOrder,
    total,
  } = props

  const isSortableTable = !!onSort

  const getClassName = (
    column: RowDefinitionType,
    row: GenericTableRow["type"],
  ) => {
    const valueToCompare =
      typeof row[column.name as keyof typeof row] === "number"
        ? Number(row[column.name as keyof typeof row])
        : 0

    const shouldBeRed = valueToCompare < 0 && column.isColored
    const shouldBeGreen = valueToCompare > 0 && column.isColored

    return { shouldBeGreen, shouldBeRed }
  }
  const result = useMemo(() => {
    if (!!total) {
      return [total].concat(data)
    }

    return data
  }, [data, total])

  const cellWidth = columnWidth / cellsPerColumn + 0.5

  const emptyData = result?.length === 0

  return (
    <Layout
      isSortableTable={isSortableTable}
      isContentReady={!isLoading && !hasError}
    >
      <table className={isSortableTable ? styles.tableMapView : styles.table}>
        <thead className={styles.headContainer}>
          <tr className={styles.row}>
            {rows?.map((cell: RowDefinitionType, id: number) => (
              <th
                key={id}
                onClick={() => {
                  if (isSortableTable && cell?.sortBy) onSort?.(cell?.sortBy)
                }}
                className={cx(
                  styles.cell({ gap: gap }),
                  cell?.sortBy
                    ? css`
                        cursor: pointer;
                      `
                    : "",
                )}
                style={{
                  width: cell.customWidth ?? cellWidth,
                  display: "flex",
                  justifyContent: cell.align || "flex-end",
                }}
              >
                <div
                  className={cx({ [styles.headersWithArrow]: !!cell?.sortBy })}
                  style={{
                    width: 80,
                  }}
                >
                  {cell.header}
                  {cell?.sortBy && (
                    <ArrowDropDown
                      className={cx(styles.descending, {
                        [styles.ascending]:
                          selectedSortOrder === "ASC" &&
                          selectedSortField === cell.sortBy,
                        [styles.activeColumn]:
                          selectedSortField === cell.sortBy,
                      })}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody
          className={cx(styles.body, standardScrollBar.root, {
            [styles.centered]: emptyData,
          })}
        >
          {emptyData ? (
            <EmptyResponse />
          ) : (
            result?.map((row: GenericTableRow["type"], rowId: number) => (
              <div
                key={rowId}
                className={
                  !!total && rowId === 0 ? styles.stickyRow : styles.row
                }
              >
                <tr className={styles.row} key={rowId}>
                  {rows.map((cell: RowDefinitionType, cellId: number) => {
                    return (
                      <td
                        key={cellId}
                        className={cx(styles.cell({ gap: gap }), {
                          [styles.coloredGreen]: getClassName(cell, row)
                            .shouldBeGreen,

                          [styles.coloredRed]: getClassName(cell, row)
                            .shouldBeRed,
                        })}
                        style={{
                          width: cell.customWidth ?? cellWidth,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: cell.align || "flex-end",
                        }}
                      >
                        {cell.render(row, rowId)}
                      </td>
                    )
                  })}
                </tr>
                {!!total && rowId === 0 && (
                  <Divider
                    style={{
                      backgroundColor: "#2B3035",
                      margin: "16px 0px",
                    }}
                  />
                )}
              </div>
            ))
          )}
        </tbody>
      </table>
    </Layout>
  )
}

const styles = {
  colored: css`
    color: var(--white);
  `,
  coloredGreen: css`
    color: var(--primary-main-light-green);
  `,
  coloredRed: css`
    color: var(--red);
  `,
  headContainer: css`
    background-color: transparent;
    z-index: 2;
  `,
  headersWithArrow: css`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  row: css`
    height: 30px;
    display: flex;
  `,
  stickyRow: css`
    position: sticky;
    top: 0;
    background-color: var(--extra-dark-grey-2);
    z-index: 2;
  `,
  table: css`
    color: var(--white);
    td,
    th {
      text-align: right;
      font-size: 12px;
    }
    th {
      font-size: 10px;
    }

    thead tr:hover {
      background-color: var(--extra-dark-grey2);
    }
    tr:hover {
      background-color: var(--extra-dark-grey);
      vertical-align: center;
    }
  `,
  body: css`
    max-height: 244px;
    display: block;
    overflow-y: scroll;
  `,
  tableMapView: css`
    color: var(--white);
    height: 100%;
    width: 100%;

    td,
    th {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
    }
    th {
      font-size: 10px;
    }
  `,
  cell: ({ gap }: { gap: number }) => css`
    padding-right: ${gap}px;
  `,
  ascending: css`
    transform: rotate(180deg);
  `,
  descending: css`
    color: var(--grey);
  `,
  activeColumn: css`
    color: var(--dark-grey);
  `,
  centered: css`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
}
